import { useEffect, useState } from 'react'
import type {
  CharacterSheet,
  SheetSpell,
  SpellAction,
  SpellProblem,
} from '@rollcall/contracts'
import type { Api } from '../api.js'
import {
  castingTimeText,
  componentsText,
  durationText,
  rangeText,
} from '../spell-facts.js'
import { Description } from './description.js'
import { useT } from './i18n-context.js'
import { contentName } from './labels.js'
import { ProvenanceProvider, ValueButton } from './provenance.js'

/**
 * Вкладка «Заклинания».
 *
 * Ячейки, подготовленное и сосредоточение приходят свёрткой журнала
 * (D4); всё, что здесь делают, уходит командой (D3). Сколько ячеек
 * и заклинаний положено, знает таблица класса — не этот компонент.
 *
 * У незаклинателя вкладка объясняет, почему пусто: пустой экран
 * читается как поломка, а «класс не колдует» — как ответ.
 *
 * Названия заклинаний переводятся словарём терминов, параметры
 * шапки — разбором (`spell-facts.ts`), описания — переводом контента
 * (D32). Непереведённое описание показывается по-английски
 * и помечено `lang="en"`.
 */

export type SpellsTabProps = {
  readonly api: Api
  readonly sheet: CharacterSheet
  readonly onAction: (action: SpellAction) => void
  readonly busy: boolean
  readonly problems: readonly SpellProblem[]
  readonly failure: string | null
}

export function SpellsTab({
  api,
  sheet,
  onAction,
  busy,
  problems,
  failure,
}: SpellsTabProps) {
  const t = useT()
  const book = sheet.spells
  const [available, setAvailable] = useState<readonly SheetSpell[] | null>(null)
  const [filter, setFilter] = useState<number | null>(null)

  const characterId = sheet.character.id
  useEffect(() => {
    if (!book.caster) return
    let cancelled = false
    void api
      .classSpells(characterId)
      .then((list) => {
        if (!cancelled) setAvailable(list)
      })
      .catch(() => {
        if (!cancelled) setAvailable([])
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId, book.caster])

  if (!book.caster) {
    // Имя класса — из листа: вкладка не знает списка классов
    // и знать его не должна.
    const chosen = Object.entries(sheet.sources).find(
      ([, source]) => source.type === 'class',
    )
    return (
      <section className="app-screen" aria-labelledby="spells-title">
        <h2 id="spells-title">{t('spells.title')}</h2>
        <p data-testid="spells-none">
          {chosen === undefined
            ? t('spells.noneNoClass')
            : t('spells.none', { class: contentName(t, chosen[0], chosen[1].name) })}
        </p>
      </section>
    )
  }

  const known = [...book.cantrips, ...book.prepared]
  const levels = [0, ...book.slots.map((slot) => slot.level)]
  const matches = (spell: SheetSpell) => filter === null || spell.level === filter

  return (
    <section className="app-screen" aria-labelledby="spells-title">
      <h2 id="spells-title">{t('spells.title')}</h2>

      {failure !== null && (
        <p className="app-problems" role="alert">
          {failure}
        </p>
      )}

      {book.saveDcPath !== null && book.attackPath !== null && (
        <ProvenanceProvider view={{ values: sheet.values, sources: sheet.sources }}>
          <section aria-labelledby="spells-numbers">
            <h3 id="spells-numbers">{t('spells.numbers')}</h3>
            <p className="app-slot-hint">
              {t('spells.abilityIs', {
                ability: t(`abilities.${book.ability ?? 'intelligence'}`),
              })}
            </p>
            <div className="app-grid">
              <ValueButton path={book.saveDcPath} label={t('spells.saveDc')} />
              <ValueButton path={book.attackPath} label={t('spells.attack')} signed />
            </div>
          </section>
        </ProvenanceProvider>
      )}

      <section aria-labelledby="spells-slots">
        <h3 id="spells-slots">{t('spells.slots')}</h3>
        <ul className="app-list app-slots">
          {book.slots.map((slot) => (
            <li key={slot.level} className="app-slot-row" data-slot={slot.level}>
              <span className="app-value-label">
                {t('spells.slotLevel', { level: slot.level })}
              </span>
              <span className="app-value-number">
                {t('spells.slotsOf', {
                  left: slot.total - slot.used,
                  total: slot.total,
                })}
              </span>
              <span className="app-actions">
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  disabled={busy}
                  onClick={() => {
                    onAction({ kind: 'spend', level: slot.level })
                  }}
                >
                  {t('spells.spend')}
                </button>
                <button
                  type="button"
                  className="app-button app-button-quiet"
                  disabled={busy}
                  onClick={() => {
                    onAction({ kind: 'restore', level: slot.level })
                  }}
                >
                  {t('spells.restore')}
                </button>
              </span>
            </li>
          ))}
        </ul>
        <Problems problems={problems} />
      </section>

      <section aria-labelledby="spells-concentration">
        <h3 id="spells-concentration">{t('spells.concentration')}</h3>
        <p data-testid="concentration">
          {book.concentration === null
            ? t('spells.concentrationNone')
            : t('spells.concentrationNow', {
                spell: contentName(t, book.concentration),
              })}
        </p>
        {book.concentration !== null && (
          <div className="app-actions">
            <button
              type="button"
              className="app-button app-button-quiet"
              disabled={busy}
              onClick={() => {
                onAction({ kind: 'concentrate', spell: null })
              }}
            >
              {t('spells.concentrationDrop')}
            </button>
          </div>
        )}
      </section>

      <section aria-labelledby="spells-known">
        <h3 id="spells-known">
          {/* Как класс называет свой список — слово из записи; перевод
              ищется по нему же, а сама запись остаётся запасным
              вариантом: новый класс покажется по-английски,
              но покажется. */}
          {book.label === null
            ? t('spells.prepared')
            : t(`spells.labelOf.${book.label}`, { defaultValue: book.label })}
        </h3>
        <p className="app-slot-hint">
          {t('spells.counts', {
            cantrips: book.cantrips.length,
            cantripsMax: book.cantripsKnown,
            prepared: book.prepared.length,
            preparedMax: book.preparedMax,
          })}
        </p>

        <LevelFilter levels={levels} filter={filter} onChange={setFilter} />

        {known.filter(matches).length === 0 ? (
          <p>{t('tabs.empty')}</p>
        ) : (
          <SpellList
            spells={known.filter(matches)}
            concentration={book.concentration}
            busy={busy}
            onAction={onAction}
            prepared
          />
        )}
      </section>

      <section aria-labelledby="spells-available">
        <h3 id="spells-available">{t('spells.available')}</h3>
        <p className="app-slot-hint">{t('spells.availableHint')}</p>
        {/* Когда набор вообще меняют — правило класса, и сказать
            о нём надо до нажатия: у жреца это длинный отдых,
            у чародея — уровень (с. 51, 57, 62, 74, 76, 79, 82). */}
        <p className="app-slot-hint" data-testid="change-hint">
          {t(
            book.changeOn === 'level'
              ? 'spells.changeHintLevel'
              : 'spells.changeHintRest',
          )}
        </p>
        {available === null ? (
          <p>{t('spells.loading')}</p>
        ) : filter === null ? (
          // Список класса у волшебника — двести с лишним записей,
          // и вываливать их все разом означает страницу, по которой
          // нельзя листать. Круг выбирается фильтром выше — тем же,
          // что и у подготовленного.
          <p data-testid="available-hint">
            {t('spells.availableCount', { count: available.length })}
          </p>
        ) : (
          <SpellList
            spells={available
              .filter(matches)
              .filter((spell) => !known.some((item) => item.id === spell.id))}
            concentration={book.concentration}
            busy={busy}
            onAction={onAction}
            prepared={false}
          />
        )}
      </section>
    </section>
  )
}

/**
 * Список заклинаний группами по кругам, внутри круга — по алфавиту.
 *
 * Тематических групп («атака», «поддержка») в источнике нет,
 * и выдумывать их значит спорить с ним на каждой записи. Круг —
 * то, чем заклинания разделены в самом источнике, и то, по чему
 * их ищут за столом: сначала «что у меня второго круга».
 *
 * Прокрутка — внутри списка: двести записей класса иначе уносят
 * страницу вниз вместе с кнопками.
 */
function SpellList({
  spells,
  concentration,
  prepared,
  busy,
  onAction,
}: {
  readonly spells: readonly SheetSpell[]
  readonly concentration: string | null
  readonly prepared: boolean
  readonly busy: boolean
  readonly onAction: (action: SpellAction) => void
}) {
  const t = useT()
  const levels = [...new Set(spells.map((spell) => spell.level))].sort(
    (left, right) => left - right,
  )

  return (
    <div className="app-scroll-list" data-testid="spell-list">
      {levels.map((level) => (
        <section key={level} aria-label={circleTitle(t, level)}>
          <h4 className="app-group-title">{circleTitle(t, level)}</h4>
          <ul className="app-list app-spells">
            {spells
              .filter((spell) => spell.level === level)
              .slice()
              .sort((left, right) => left.name.localeCompare(right.name))
              .map((spell) => (
                <SpellCard
                  key={spell.id}
                  spell={spell}
                  concentration={concentration}
                  busy={busy}
                  onAction={onAction}
                  prepared={prepared}
                />
              ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function circleTitle(t: ReturnType<typeof useT>, level: number): string {
  return level === 0 ? t('spells.cantrips') : t('spells.circle', { level })
}

function Problems({ problems }: { readonly problems: readonly SpellProblem[] }) {
  const t = useT()
  if (problems.length === 0) return null
  return (
    <>
      {problems.map((problem) => (
        <p
          key={`${problem.code}:${problem.subject ?? ''}`}
          className="app-problems"
          role="alert"
        >
          {t(`spells.refusal.${problem.code}`)}
        </p>
      ))}
    </>
  )
}

/** Фильтр по кругу: заговоры и те круги, ячейки которых есть. */
function LevelFilter({
  levels,
  filter,
  onChange,
}: {
  readonly levels: readonly number[]
  readonly filter: number | null
  readonly onChange: (value: number | null) => void
}) {
  const t = useT()
  return (
    <fieldset className="app-slot">
      <legend className="app-slot-legend">{t('spells.filter')}</legend>
      <div className="app-slot-options">
        <label className="app-option">
          <input
            type="radio"
            name="spell-level"
            checked={filter === null}
            onChange={() => {
              onChange(null)
            }}
          />
          <span>{t('spells.filterAll')}</span>
        </label>
        {levels.map((level) => (
          <label key={level} className="app-option">
            <input
              type="radio"
              name="spell-level"
              checked={filter === level}
              onChange={() => {
                onChange(level)
              }}
            />
            <span>
              {level === 0
                ? t('spells.cantripShort')
                : t('spells.levelShort', { level })}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

/**
 * Карточка заклинания: круг, время, дистанция, компоненты,
 * длительность и описание из записи.
 *
 * Параметры шапки приходят строками источника («Action», «60 feet»)
 * и переводятся разбором (`spell-facts.ts`). Описание, у которого
 * перевода нет, остаётся английским и помечается `lang="en"` —
 * иначе синтезатор речи прочтёт его по-русски.
 */
function SpellCard({
  spell,
  concentration,
  prepared,
  busy,
  onAction,
}: {
  readonly spell: SheetSpell
  readonly concentration: string | null
  readonly prepared: boolean
  readonly busy: boolean
  readonly onAction: (action: SpellAction) => void
}) {
  const t = useT()
  const [open, setOpen] = useState(false)

  return (
    <li className="app-spell" data-spell={spell.id} data-prepared={prepared}>
      <div className="app-inventory-head">
        <span className="app-value-label">{contentName(t, spell.id, spell.name)}</span>
        <span className="app-value-note">
          {spell.level === 0
            ? t('spells.cantripShort')
            : t('spells.levelShort', { level: spell.level })}
          {spell.concentration && ` · ${t('spells.concentration')}`}
          {spell.ritual && ` · ${t('spells.ritual')}`}
        </span>
      </div>

      <div className="app-actions">
        <button
          type="button"
          className="app-button app-button-quiet"
          disabled={busy}
          onClick={() => {
            onAction(
              prepared
                ? { kind: 'unprepare', spell: spell.id }
                : { kind: 'prepare', spell: spell.id },
            )
          }}
        >
          {t(prepared ? 'spells.remove' : 'spells.add')}
        </button>

        {prepared && spell.concentration && (
          <button
            type="button"
            className="app-button app-button-quiet"
            disabled={busy || concentration === spell.id}
            onClick={() => {
              onAction({ kind: 'concentrate', spell: spell.id })
            }}
          >
            {t('spells.concentrateOn')}
          </button>
        )}

        <button
          type="button"
          className="app-button app-button-quiet"
          aria-expanded={open}
          onClick={() => {
            setOpen(!open)
          }}
        >
          {t('spells.details')}
        </button>
      </div>

      {/* Предупреждение показывается до нажатия, а не после отказа:
          новое сосредоточение снимет прежнее, и узнать об этом
          постфактум — потерять заклинание (с. 186). */}
      {prepared &&
        spell.concentration &&
        concentration !== null &&
        concentration !== spell.id && (
          <p className="app-slot-hint">
            {t('spells.concentrationReplaces', {
              spell: contentName(t, concentration),
            })}
          </p>
        )}

      {open && (
        <dl className="app-spell-facts">
          <dt>{t('spells.school')}</dt>
          {/* Школа — термин, а не описание: переводится, как классы
              и навыки. Всё остальное в карточке — строки источника. */}
          <dd>
            {t(`spells.schoolOf.${spell.school}`, { defaultValue: spell.school })}
          </dd>
          <dt>{t('spells.castingTime')}</dt>
          {/* Параметры разбираются и переводятся (`spell-facts.ts`):
              в источнике это строка на поле, а карточка русская. */}
          <dd>{castingTimeText(t, spell.castingTime)}</dd>
          <dt>{t('spells.range')}</dt>
          <dd>{rangeText(t, spell.range)}</dd>
          <dt>{t('spells.components')}</dt>
          <dd>{componentsText(t, spell.components)}</dd>
          <dt>{t('spells.duration')}</dt>
          <dd>{durationText(t, spell.duration)}</dd>
        </dl>
      )}
      {/* Описание: русское основное, оригинал по кнопке (D32).
          Один и тот же вид на всём листе — компонент общий. */}
      {open && <Description text={spell.text} textRu={spell.textRu} />}
    </li>
  )
}
